import React from 'react';

export interface ProgressCircularProps {
  /** Progress value (0–100). Ignored if indeterminate=true */
  value?: number;
  /** Outer size in pixels (width & height) */
  size?: number;
  /** Stroke width */
  width?: number;
  /** Whether to show the spinning indeterminate animation */
  indeterminate?: boolean;
  /** Tailwind class for progress stroke color */
  colorClassName?: string;
  /** Tailwind class for the background track */
  bgClassName?: string;
  /** Whether to show a numeric label inside */
  showLabel?: boolean;
  /** Additional wrapper classes */
  className?: string;
}

const ProgressCircular: React.FC<ProgressCircularProps> = ({
  value = 0,
  size = 64,
  width = 6,
  indeterminate = false,
  colorClassName = 'stroke-blue-500',
  bgClassName = 'stroke-gray-200',
  showLabel = false,
  className = '',
}) => {
  // Ensure value is within 0–100
  const clampedValue = Math.min(100, Math.max(0, value));

  // Geometry math
  const radius = (size - width) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (clampedValue / 100) * circumference;

  return (
    <div
      className={`relative inline-flex items-center justify-center ${className}`}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        className="rotate-[-90deg]" // start from top like Vuetify
      >
        {/* Track circle */}
        <circle className={bgClassName} strokeWidth={width} fill="transparent" r={radius} cx={size / 2} cy={size / 2} />
        {/* Progress circle */}
        <circle
          className={`${colorClassName} transition-[stroke-dashoffset] duration-300 ease-out ${
            indeterminate ? 'animate-spin' : ''
          }`}
          style={{ transformOrigin: 'center', transformBox: 'fill-box' }}
          strokeWidth={width}
          strokeLinecap="round"
          fill="transparent"
          r={radius}
          cx={size / 2}
          cy={size / 2}
          strokeDasharray={circumference}
          strokeDashoffset={indeterminate ? circumference * 0.25 : offset}
        />
      </svg>

      {showLabel && !indeterminate && (
        <span className="absolute text-xs font-medium text-gray-700">{Math.round(clampedValue)}%</span>
      )}
    </div>
  );
};

export default ProgressCircular;
